/**
 * `lib/returnTo.ts` — come back to the page you opened after signing in
 * (#2071, widened to every in-app address by #2240).
 *
 * An unauthenticated visit to a deep route is sent to `/sign-in`, and every
 * post-sign-in path (the custom page's `setActive`, Google's return to `/`,
 * the dev picker) lands on `/` — so the address the person opened, query
 * string and all, was lost. #2071 kept it for the Telegram links
 * (`/connect/telegram?t=…`, `/add-login?t=…`) only; #2240 found a roster
 * deep link (`/teams/<id>/roster?from=…&to=…`) dropped the same way, so
 * now every same-origin address is kept.
 *
 * The address travels two ways, so either can carry it on its own:
 *   - `?redirect_url=` on `/sign-in` (`signInPathFor`) — visible, survives
 *     a reload or a copied link. Same name Clerk's own card reads.
 *   - `sessionStorage` (`stashReturnTo`) — survives the Google redirect and
 *     the email-code steps; it dies with the tab. `consumeReturnTo` pops it
 *     once after sign-in.
 *
 * Every read goes through `safeReturnTarget`: only a same-origin path
 * (a relative `/…`, or an absolute URL on this origin, reduced to its
 * path + query + hash) is ever returned — never another site (no open
 * redirect on stale or crafted data) and never an auth page (no loop).
 * The check runs on the NORMALISED path too: `/..//evil.com` normalises
 * to `//evil.com`, which a browser reads as another host.
 */

const KEY = "tarii.return_to";
/** When the stash was written (ms since epoch) — how fresh it is. */
const KEY_AT = "tarii.return_to.at";
/** When the "already signed in" step last forwarded on its own (loop guard). */
const KEY_AUTO_AT = "tarii.return_to.auto_at";

/** A stash older than this is not followed without a click (#2240). */
export const FRESH_STASH_MS = 5 * 60 * 1000;
/** The signed-in step forwards on its own at most once per this window (#2240). */
export const AUTO_FORWARD_WINDOW_MS = 10 * 1000;

/** The query parameter `/sign-in` carries the return address in. */
export const RETURN_PARAM = "redirect_url";

/** Where the Telegram chat's Connect Tarii link lands (#2071). */
export const CONNECT_TELEGRAM_PATH = "/connect/telegram";

/** Where the Mini App's **Add a login** lands (#2071 S3). */
export const ADD_LOGIN_PATH = "/add-login";

/** Auth pages are never a place to come back to — returning there loops. */
const AUTH_PATHS: ReadonlyArray<string> = ["/sign-in", "/sign-up", "/sso-callback"];

function currentOrigin(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.origin;
}

/**
 * `target` as a same-origin `path?query#hash`, or `null` when it is not
 * safe to send someone to: empty, protocol-relative (`//evil`), a
 * backslash trick (`/\evil`), another origin, a non-http(s) scheme, or an
 * auth page.
 */
export function safeReturnTarget(target: string | null | undefined): string | null {
  const once = normaliseReturnTarget(target);
  // Stable under a second pass, or refused: whatever normalisation
  // produced must itself be a plain same-origin path.
  if (once === null || normaliseReturnTarget(once) !== once) return null;
  return once;
}

function normaliseReturnTarget(target: string | null | undefined): string | null {
  if (!target) return null;
  const raw = target.trim();
  if (raw === "" || raw.includes("\\")) return null;
  const origin = currentOrigin();
  if (!origin) return null;
  const relative = raw.startsWith("/") && !raw.startsWith("//");
  let resolved: URL;
  try {
    // A relative path resolves against this origin; anything else must
    // already be an absolute URL (Clerk writes `redirect_url` that way).
    resolved = relative ? new URL(raw, origin) : new URL(raw);
  } catch {
    return null;
  }
  if (resolved.origin !== origin) return null;
  const path = resolved.pathname;
  // `/..//evil.com` → pathname `//evil.com`: a protocol-relative address.
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return null;
  if (AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  return `${path}${resolved.search}${resolved.hash}`;
}

/** The `/sign-in` address that brings the person back to `target` after. */
export function signInPathFor(target: string): string {
  const safe = safeReturnTarget(target);
  if (!safe || safe === "/") return "/sign-in";
  return `/sign-in?${new URLSearchParams({ [RETURN_PARAM]: safe }).toString()}`;
}

/** The return address carried on a `/sign-in` URL's query, validated. */
export function returnTargetFromSearch(search: string): string | null {
  return safeReturnTarget(new URLSearchParams(search).get(RETURN_PARAM));
}

/** Remember `target` (path + query) to come back to after sign-in. */
export function stashReturnTo(target: string): void {
  const safe = safeReturnTarget(target);
  if (!safe || safe === "/") return;
  try {
    window.sessionStorage.setItem(KEY, safe);
    window.sessionStorage.setItem(KEY_AT, String(Date.now()));
  } catch {
    // Private mode: the `?redirect_url=` copy still carries it.
  }
}

/** The stashed address without clearing it; `null` when none or not allowed. */
export function peekReturnTo(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return safeReturnTarget(window.sessionStorage.getItem(KEY));
  } catch {
    return null;
  }
}

/**
 * The stashed address only if it was written in the last `maxAgeMs` —
 * not one left from earlier in the tab. Not cleared.
 */
export function peekFreshReturnTo(maxAgeMs: number = FRESH_STASH_MS): string | null {
  if (typeof window === "undefined") return null;
  try {
    const at = Number(window.sessionStorage.getItem(KEY_AT));
    if (!Number.isFinite(at) || at <= 0 || Date.now() - at > maxAgeMs) return null;
  } catch {
    return null;
  }
  return peekReturnTo();
}

/** The stashed address, once (cleared on read); `null` when none or not allowed. */
export function consumeReturnTo(): string | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KEY);
    if (raw !== null) window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(KEY_AT);
  } catch {
    return null;
  }
  return safeReturnTarget(raw);
}

/**
 * Loop guard for the "already signed in" step's automatic forward
 * (#2240): `true` (and the moment recorded) when it has not forwarded in
 * the last `windowMs`; `false` otherwise, or when storage is unavailable
 * — then the page waits for a click. A Clerk session the app does not
 * count as signed in (a pending session task) would otherwise bounce
 * gate → sign-in → forward → gate forever.
 */
export function claimAutoForward(windowMs: number = AUTO_FORWARD_WINDOW_MS): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(window.sessionStorage.getItem(KEY_AUTO_AT));
    const now = Date.now();
    if (Number.isFinite(last) && last > 0 && now - last < windowMs) return false;
    window.sessionStorage.setItem(KEY_AUTO_AT, String(now));
    return true;
  } catch {
    return false;
  }
}
